'use client'

import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react'
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock'
import { Fragment, useState, useEffect, useRef } from 'react'
import Link from './Link'
import headerNavLinks from '@/data/headerNavLinks'
import { usePathname } from 'next/navigation'

const MobileNav = () => {
  const [navShow, setNavShow] = useState(false)
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null)
  const [isScrolled, setIsScrolled] = useState(false)
  const navRef = useRef(null)
  const pathname = usePathname()
  const [isPhoneVisible, setIsPhoneVisible] = useState(true)

  // Determine if we're on a legal page
  const isLegalPage = pathname === '/datenschutzrichtlinie' || pathname === '/impressum'

  // Toggle nav open/close (and lock body scroll)
  const onToggleNav = () => {
    setNavShow((status) => {
      if (status) {
        enableBodyScroll(navRef.current!)
      } else {
        disableBodyScroll(navRef.current!)
      }
      return !status
    })
  }

  // Submenu toggler
  const toggleSubmenu = (title: string) => {
    setOpenSubmenu((current) => (current === title ? null : title))
  }

  // Clean up body-scroll-lock on unmount
  useEffect(() => clearAllBodyScrollLocks, [])
  useEffect(() => {
    setIsPhoneVisible(!openSubmenu)
  }, [openSubmenu])

  // Listen for scroll to set isScrolled
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 0)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <>
      {/* Hamburger Button */}
      <button aria-label="Toggle Menu" onClick={onToggleNav} className="lg:hidden">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className={`h-8 w-8 hover:text-[#ff4500] ${
            isScrolled
              ? 'text-gray-600'
              : isLegalPage
                ? 'text-gray-900 dark:text-gray-100'
                : 'text-white'
          } `}
        >
          <path
            fillRule="evenodd"
            d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {/* Mobile Menu */}
      <Transition appear show={navShow} as={Fragment} unmount={false}>
        <Dialog as="div" onClose={onToggleNav} unmount={false}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            unmount={false}
          >
            <div className="fixed inset-0 z-60 bg-black/25" />
          </TransitionChild>

          <TransitionChild
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="translate-x-full opacity-0"
            enterTo="translate-x-0 opacity-95"
            leave="transition ease-in duration-200 transform"
            leaveFrom="translate-x-0 opacity-95"
            leaveTo="translate-x-full opacity-0"
            unmount={false}
          >
            <DialogPanel className="fixed top-0 left-0 z-70 h-full w-full max-w-[100vw] bg-white/95 dark:bg-gray-950/98">
              <nav
                ref={navRef}
                className="mt-8 flex h-full flex-col items-start overflow-x-hidden overflow-y-auto pt-2 pl-12 text-left"
              >
                {headerNavLinks.map(
                  (link) =>
                    link.title !== '+49 203 8039 3070' && (
                      <div
                        key={link.title}
                        className={`mb-4 ${link.isPhone ? 'hidden sm:block' : ''}`}
                      >
                        {'submenu' in link ? (
                          <>
                            <button
                              onClick={() => toggleSubmenu(link.title)}
                              className="flex items-center space-x-2 py-2 pr-4 text-2xl font-bold tracking-widest text-gray-900 outline-none hover:text-[#ff4500] dark:text-gray-100 dark:hover:text-[#ff4500]"
                            >
                              <span>{link.title}</span>
                              <svg
                                className={`h-6 w-6 transform transition-transform ${
                                  openSubmenu === link.title ? 'rotate-180' : ''
                                }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </button>
                            {openSubmenu === link.title && link.submenu && (
                              <div className="mt-2 ml-4 space-y-2">
                                {link.submenu.map((subItem) => (
                                  <Link
                                    key={subItem.href}
                                    href={subItem.href}
                                    className="block py-2 text-xl font-medium text-gray-700 hover:text-[#ff4500] dark:text-gray-300 dark:hover:text-[#ff4500]"
                                    onClick={onToggleNav}
                                    {...(subItem.openInNewTab && {
                                      target: '_blank',
                                      rel: 'noopener noreferrer',
                                    })}
                                  >
                                    {subItem.title}
                                  </Link>
                                ))}
                              </div>
                            )}
                          </>
                        ) : (
                          <Link
                            href={link.href || '#'}
                            className="block py-2 pr-4 text-2xl font-bold tracking-widest text-gray-900 outline-none hover:text-[#ff4500] dark:text-gray-100 dark:hover:text-[#ff4500]"
                            onClick={onToggleNav}
                            {...(link.openInNewTab && {
                              target: '_blank',
                              rel: 'noopener noreferrer',
                            })}
                          >
                            {link.title}
                          </Link>
                        )}
                      </div>
                    )
                )}
              </nav>

              {/* Phone Number */}
              <Link
                href="tel:+49302239953879"
                className={`fixed bottom-8 left-12 flex items-center space-x-2 py-2 text-xl font-medium text-gray-900 transition-all duration-20 hover:text-[#ff4500] dark:text-gray-100 ${isPhoneVisible ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-10 opacity-0'}`}
                onClick={onToggleNav}
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
                <span>030 223 995 3879</span>
              </Link>

              {/* Close Button */}
              <button
                aria-label="Toggle Menu"
                onClick={onToggleNav}
                className="fixed top-7 right-4 z-80 h-16 w-16 p-4 hover:text-[#ff4500]"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className={`${isScrolled ? 'text-black hover:text-[#ff4500]' : 'text-gray-900 hover:text-[#ff4500] dark:text-gray-100'} h-full w-full`}
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414
                       1.414L11.414 10l4.293 4.293a1 1 0 01-1.414
                       1.414L10 11.414l-4.293 4.293a1 1
                       0 01-1.414-1.414L8.586 10 4.293
                       5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </Transition>
    </>
  )
}

export default MobileNav
